/**
 * Encodes a string for safe use in HTML attributes
 * 
 * This prevents XSS when embedding user data in:
 * - HTML attribute values
 * - Data attributes
 * - ARIA attributes
 * 
 * @param value - The string to encode
 * @returns HTML attribute-safe encoded string
 * 
 * @example
 * const userInput = '" onclick="alert(\'XSS\')" data-value="';
 * const safe = encodeHtmlAttribute(userInput);
 * // safe = "&quot; onclick=&quot;alert(&#x27;XSS&#x27;)&quot; data-value=&quot;"
 * 
 * // Use: <div title="${safe}">
 */
export const encodeHtmlAttribute = (
  value: string | null | undefined
): string => {
  if (!value) {
    return '';
  }

  const str = String(value);

  return str
    .replace(/&/g, '&amp;')      // Ampersand (must be first)
    .replace(/</g, '&lt;')       // Less than
    .replace(/>/g, '&gt;')       // Greater than
    .replace(/"/g, '&quot;')     // Double quote
    .replace(/'/g, '&#x27;')     // Single quote (HTML5 entity)
    .replace(/\//g, '&#x2F;')    // Forward slash
    .replace(/`/g, '&#x60;')     // Backtick
    .replace(/=/g, '&#x3D;')     // Equals
    .replace(/\n/g, '&#10;')     // Newline
    .replace(/\r/g, '&#13;')     // Carriage return
    .replace(/\t/g, '&#9;')      // Tab
    .replace(/\0/g, '');         // Remove null bytes
};

/**
 * Validates an HTML attribute name
 * 
 * @param attrName - The attribute name to validate
 * @returns Validated attribute name or empty string if invalid
 * 
 * @example
 * const userInput = "onclick";
 * const safe = validateAttributeName(userInput);
 * // safe = "" (blocked - event handler)
 */
export const validateAttributeName = (
  attrName: string | null | undefined
): string => {
  if (!attrName) {
    return '';
  }

  const name = String(attrName).toLowerCase().trim();

  if (name.startsWith('on')) {
    return '';
  }

  const dangerous = [
    'src',
    'href',
    'action',
    'formaction',
    'xlink:href',
    'dynsrc',
    'lowsrc',
  ];

  if (dangerous.includes(name)) {
    return '';
  }

  // Only allow valid HTML attribute name characters
  if (/^[a-z][a-z0-9\-_:.]*$/i.test(name)) {
    return name;
  }

  return '';
};

/**
 * Encodes a value for safe use in data attributes
 * 
 * @param value - The value to encode
 * @returns Encoded value safe for data attributes
 * 
 * @example
 * const userInput = { name: "<script>alert('XSS')</script>" };
 * const safe = encodeDataAttribute(userInput);
 * // safe = "{&quot;name&quot;:&quot;&lt;script&gt;alert(&#x27;XSS&#x27;)&lt;/script&gt;&quot;}"
 * 
 * // Use: <div data-user="${safe}">
 */
export const encodeDataAttribute = (value: any): string => {
  if (value == null) {
    return '';
  }

  // Convert to JSON string first
  const jsonStr = JSON.stringify(value);

  // Then encode for HTML attribute
  return encodeHtmlAttribute(jsonStr);
};

